import { useEffect, useState } from "react";
import API from "@app/services/api";

/** Une question du questionnaire, telle que le formulaire source l'imprime. */
export interface QuestionnaireItem {
  key: string;
  wording: string;
  /** Cotation maximale de la question. Absente sur une question qui ne compte pas. */
  max?: number;
  /** Code de l'algorithme SF-12 ; absent pour les questionnaires à somme simple. */
  construct?: string;
  /**
   * `false` = question affichée qui ne compte pas dans le total.
   *
   * Le score de réussite demande la date de reprise du travail sur la même feuille que ses sept
   * questions cotées : c'est une réponse du patient, elle a sa place sous le questionnaire, et
   * elle ne doit pas bouger le score.
   */
  scored?: boolean;
  /**
   * `true` = la source accepte « NR » ici, parce que la question ne s'appliquait pas.
   *
   * Le total est alors pris sur les questions qui s'appliquaient, rapporté au maximum que
   * CELLES-LÀ pouvaient atteindre. Quatre des sept questions du score de réussite sont dans ce
   * cas — travail, délai de reprise, sport, vie sexuelle — et 43 passations sur 53 en portent au
   * moins une : les compter comme sans réponse effacerait quatre passations sur cinq.
   */
  optional?: boolean;
  /** `"date"` = la réponse est une date, lue telle quelle et jamais cotée. */
  kind?: string;
}

/** Un score produit par un questionnaire. Le SF-12 en produit deux, l'Oswestry un seul. */
export interface QuestionnaireScore {
  field_key: string;
  label: string;
  /** Le total que les réponses impliquent. `null` si le questionnaire est incomplet. */
  computed: number | null;
  /** Le total que la source a écrit à côté des réponses, quand elle en écrit un. */
  source: number | null;
  /**
   * Le même total, ramené sur l'échelle du champ.
   *
   * C'est LUI qui décide d'un désaccord, jamais `source`. Le NDI imprime « 17 » sur 50 là où le
   * champ est un pourcentage : comparer les deux tels quels signalerait chaque passation comme
   * contradictoire, et un marqueur qui s'allume partout ne signale plus rien.
   */
  source_scaled?: number | null;
  how: string;
  /**
   * L'échelle que les médecins déclarent pour ce score — `[min, max]`.
   *
   * Purement descriptive : rien ne l'applique, et c'est le but. Une borne appliquée ne prévient
   * pas, elle supprime, et un score supprimé est une question que personne ne pose. Un score qui
   * la dépasse est donc affiché, signalé, et laissé au médecin — c'est la seule manière de savoir
   * si c'est la borne qui est fausse ou la valeur.
   */
  declared_range?: [number, number] | null;
}

/** Une passation : un jour, un formulaire, les réponses et les totaux qui en découlent. */
export interface Passation {
  date: string | null;
  document_id: string;
  form: string;
  answers: Record<string, number>;
  /**
   * Les réponses qui ne comptent pas dans le total, telles que la source les écrit.
   *
   * Elles ne sont pas dans `answers` pour une raison de type : « 01/09/2025 » n'est pas un nombre,
   * et le lecteur qui transforme une cellule en cote l'effacerait.
   */
  notes?: Record<string, string>;
  /**
   * Les questions restées sans réponse sur ce formulaire. Vide sur une passation complète.
   *
   * Une question optionnelle laissée vide n'y figure pas : elle ne s'appliquait pas. La signaler
   * ferait porter la mention « incomplet » à quatre passations sur cinq du score de réussite, et
   * un marqueur qui s'allume partout ne signale plus rien.
   */
  missing?: string[];
  scores: QuestionnaireScore[];
}

export interface Questionnaire {
  id: string;
  label: string;
  items: QuestionnaireItem[];
  scores: Array<Omit<QuestionnaireScore, "computed" | "source">>;
  passations: Passation[];
}

/**
 * Les questionnaires d'un patient, indexés par le champ de score qu'ils alimentent.
 *
 * Un même questionnaire alimente parfois deux champs — les deux composantes du SF-12 sortent des
 * douze mêmes réponses. L'index les fait pointer vers la MÊME entrée : c'est ce qui évite que la
 * page patient affiche deux fois les douze questions sous deux noms de score différents.
 */
export function indexByField(
  questionnaires: Questionnaire[],
): Record<string, Questionnaire> {
  const index: Record<string, Questionnaire> = {};
  for (const questionnaire of questionnaires) {
    for (const score of questionnaire.scores) {
      if (score.field_key) index[score.field_key] = questionnaire;
    }
  }
  return index;
}

/**
 * Charge les questionnaires du dossier une seule fois, pour toute la page.
 *
 * Le chargement est volontairement porté par la page et non par chaque ligne de score : les six
 * champs du groupe « Scores » partagent une seule récupération côté source, et six lignes qui
 * appelleraient chacune l'endpoint déclencheraient six allers-retours vers le système source pour
 * exactement la même réponse.
 *
 * Un échec est silencieux et rend une liste vide : le détail est un supplément, son absence retire
 * une flèche, elle ne casse pas la page.
 */
export function usePatientQuestionnaires(
  tenantId: string | undefined,
  patientId: number | string | undefined,
): { byField: Record<string, Questionnaire>; loading: boolean } {
  const [byField, setByField] = useState<Record<string, Questionnaire>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || patientId === undefined || patientId === null) {
      setByField({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    API.get({ path: `/patient/${tenantId}/${patientId}/questionnaires` })
      .then((res: { data?: { questionnaires?: Questionnaire[] } }) => {
        if (cancelled) return;
        const list = res?.data?.questionnaires;
        setByField(indexByField(Array.isArray(list) ? list : []));
      })
      .catch(() => {
        if (!cancelled) setByField({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, patientId]);

  return { byField, loading };
}

/**
 * Somme des réponses, telle qu'elle s'écrit sous la matrice : « 11 / 50 ».
 *
 * Le dénominateur est ce que les questions réellement posées à ce patient pouvaient rapporter :
 * celles auxquelles il a répondu, plus celles auxquelles il devait répondre et ne l'a pas fait.
 * Une passation où trois questions ne s'appliquaient pas se lit « 20 / 25 » et non « 20 / 35 » —
 * c'est le même dénominateur que celui dont le pourcentage découle.
 *
 * `complete` ne dit que ceci : toutes les questions qui DEVAIENT être répondues l'ont été.
 */
export function rawSum(
  items: QuestionnaireItem[],
  answers: Record<string, number>,
): { sum: number; max: number; complete: boolean } {
  let sum = 0;
  let max = 0;
  let complete = true;
  for (const item of items) {
    if (item.scored === false) continue;
    const value = answers[item.key];
    const answered = typeof value === "number" && Number.isFinite(value);
    if (answered) sum += value;
    if (answered || item.optional !== true) max += item.max ?? 0;
    if (!answered && item.optional !== true) complete = false;
  }
  return { sum, max, complete };
}

/**
 * Une entrée de série de score telle qu'elle est stockée.
 *
 * ``items`` n'est présent que sur les passations qu'un clinicien a effectivement corrigées, et
 * c'est la seule chose qui distingue une réponse corrigée d'une réponse lue. L'omettre sur les
 * autres n'est pas une économie : le porter partout ferait dire au dossier que le médecin a
 * arbitré quatorze passations quand il en a ouvert une.
 */
export interface StoredScore {
  date: string;
  score: number;
  items?: Record<string, number>;
}

/**
 * Les réponses corrigées portées par une série stockée, indexées par date.
 *
 * C'est ce qui referme la boucle : sans ça, un médecin corrige une réponse, le total enregistré
 * change, puis il rouvre le panneau et la matrice — qui se re-dérive de la source — réaffiche
 * l'ancienne réponse sous le nouveau total. Deux chiffres qui se contredisent, et rien qui dise
 * lequel a raison.
 */
export function correctedAnswersByDate(
  value: unknown,
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  if (!Array.isArray(value)) return out;
  for (const entry of value as StoredScore[]) {
    if (entry && typeof entry === "object" && entry.date && entry.items) {
      out[String(entry.date).slice(0, 10)] = entry.items;
    }
  }
  return out;
}

/**
 * La série d'un champ de score, reconstituée depuis les passations du questionnaire.
 *
 * C'est cette série que la page affiche dès qu'un questionnaire existe, de préférence à la colonne
 * agrégée — et ce n'est pas une préférence de confort. Sur le dossier de référence, la colonne
 * porte des relevés datés sur la CONSULTATION : trois scores d'Oswestry différents retombent sur
 * `2021-10-05 10:00:00`, donc trois points au même x, dont deux invisibles. La passation, elle, est
 * datée sur le formulaire rempli, et un formulaire n'est rempli qu'une fois.
 *
 * Une passation dont le total est incalculable — questionnaire incomplet — n'entre pas dans la
 * série : elle n'a pas de valeur à porter, et lui en inventer une mettrait sur la courbe un point
 * qu'aucun document ne soutient.
 */
export function seriesForField(
  questionnaire: Questionnaire,
  fieldKey: string,
): Array<{ date: string; score: number }> {
  const out: Array<{ date: string; score: number }> = [];
  for (const passation of questionnaire.passations) {
    if (!passation.date) continue;
    const score = passation.scores.find((s) => s.field_key === fieldKey);
    if (score?.computed === null || score?.computed === undefined) continue;
    out.push({ date: passation.date, score: score.computed });
  }
  return out;
}
